import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ref, onValue, update } from "../lib/firebase-database"
import { auth, db } from "../firebase"
import { AdminLayout } from "./AdminLayout"
import { useAuth } from "../hooks/useAuth"
import { useAdminQueryCount } from "../hooks/useAdminQueryCount"
import { formatDistanceToNow } from "date-fns"
import { Mail, MessageSquare } from "lucide-react"

export default function AdminQueries() {
  const [queries, setQueries] = useState([])
  const { markQueriesAsViewed } = useAdminQueryCount()
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    const queriesRef = ref(db, "queries")
    const unsubscribe = onValue(queriesRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const queriesList = Object.entries(data).map(([id, query]) => ({
          id,
          ...query,
        }))
        queriesList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        setQueries(queriesList)
      } else {
        setQueries([])
      }
    })

    return () => {
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (queries.length > 0) {
      markQueriesAsViewed(queries.map((q) => q.id))
      const updates = {}
      queries.forEach((q) => {
        updates[`queries/${q.id}/viewed`] = true
      })
      update(ref(db), updates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries])

  const markAsRead = async (queryId) => {
    const queryRef = ref(db, `queries/${queryId}`)
    await update(queryRef, { status: "read" })
  }

  const handleLogout = () => {
    auth.signOut().then(() => navigate("/login"))
  }

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Owner Queries</h1>
          <p className="text-sm text-gray-500 mt-1">
            Messages and questions from property owners.
          </p>
        </div>

        {queries.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            <MessageSquare className="h-8 w-8 mx-auto mb-3 text-gray-300" />
            No queries yet.
          </div>
        ) : (
          <div className="space-y-3">
            {queries.map((q) => {
              const isNew = q.status === "new"
              return (
                <div
                  key={q.id}
                  className="bg-white border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {q.ownerEmail}
                      </span>
                    </div>
                    <span
                      className={`text-[11px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                        isNew
                          ? "bg-black text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {q.status || "new"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">
                    {q.message}
                  </p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      {q.createdAt
                        ? formatDistanceToNow(new Date(q.createdAt), {
                            addSuffix: true,
                          })
                        : "Just now"}
                    </span>
                    {isNew && (
                      <button
                        type="button"
                        onClick={() => markAsRead(q.id)}
                        className="text-xs font-medium text-gray-700 hover:text-black underline-offset-2 hover:underline"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
